import React, { FC } from 'react';
import { DocumentsType } from 'v2-types/order';
import {
  Box,
  capitalize,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { convertToBytes, convertUnixTimestamp } from '../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: any) => ({
  description: {
    color: theme.palette.primary.darker,
    width: 'max-content',
    textTransform: 'capitalize',
    fontWeight: 'bold',
  },
  date: {
    color: theme.palette.primary.darker,
    width: 'max-content',
  },
  mobileDescription: {
    color: theme.palette.primary.darker,
    width: 'auto',
    overflowWrap: 'anywhere',
    textTransform: 'capitalize',
    fontWeight: 'bold',
  },
  mobileDate: {
    color: theme.palette.primary.darker,
    width: 'auto',
    overflowWrap: 'anywhere',
  },
}));

type Props = {
  kind: string;
  document: DocumentsType;
  handleDownload: (link: string, fileName: string) => void;
  viewFile: (fileId: string) => void;
  editDocumentBundle: () => void;
  deleteDocument: () => void;
};

export const DocumentTableRow: FC<Props> = ({
  kind,
  document,
  handleDownload,
  viewFile,
  editDocumentBundle,
  deleteDocument,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const firstFile =
    document.archives.length && document.archives[0].files ? document.archives[0].files[0] : null;
  const cells = [
    {
      id: 'name',
      content: <Typography className={classes.description}>{document.description}</Typography>,
    },
    {
      id: 'date',
      content: (
        <Typography className={classes.date} variant="body2">
          {firstFile ? capitalize(convertUnixTimestamp(firstFile.mtime)) : t('documents:not-modified')}
        </Typography>
      ),
      hide: false,
    },
    {
      id: 'size',
      content: (
        <Typography className={classes.date} variant="body2">
          {firstFile ? convertToBytes(firstFile.size) : 'Empty'}
        </Typography>
      ),
      hide: false,
    },
    {
      id: 'options',
      content: (
        <Box style={{ display: 'flex', gap: '0.4rem' }}>
          {['zip', 'pdf'].includes(kind) && (
            <>
              <Tooltip title={t('documents:edit-content')}>
                <IconButton size="small" style={{ padding: isMobile ? theme.spacing(1.5) : undefined }} onClick={editDocumentBundle}>
                  <FontAwesomeIcon icon="edit" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" style={{ padding: isMobile ? theme.spacing(1.5) : undefined }} onClick={deleteDocument}>
                <FontAwesomeIcon icon="trash" />
              </IconButton>
            </>
          )}
          {!['zip', 'package'].includes(kind) && (
            <IconButton
              size="small"
              style={{ padding: isMobile ? theme.spacing(1.5) : undefined }}
              disabled={!firstFile}
              onClick={() => firstFile && viewFile(firstFile._id)}
            >
              <FontAwesomeIcon icon="eye" />
            </IconButton>
          )}
          <IconButton
            size="small"
            style={{ padding: isMobile ? theme.spacing(1.5) : undefined }}
            disabled={!firstFile}
            onClick={() => firstFile && handleDownload(firstFile.download, firstFile.filename)}
          >
            <FontAwesomeIcon icon="download" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const actions = cells.find((cell) => cell.id === 'options')?.content;

  return (
    <TableRow>
      {isMobile ? (
        <TableCell colSpan={4} style={{ padding: theme.spacing(1.5) }}>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1), minWidth: 0 }}>
            <Box style={{ minWidth: 0 }}>{React.cloneElement(cells[0].content as React.ReactElement, { className: classes.mobileDescription })}</Box>
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
              <Box>
                <Typography variant="caption">{t('documents:lastUpdate')}</Typography>
                {React.cloneElement(cells[1].content as React.ReactElement, { className: classes.mobileDate })}
              </Box>
              <Box>
                <Typography variant="caption">{t('documents:size')}</Typography>
                {React.cloneElement(cells[2].content as React.ReactElement, { className: classes.mobileDate })}
              </Box>
            </Box>
            {actions}
          </Box>
        </TableCell>
      ) : cells
        .filter((cell) => !cell.hide)
        .map((cell) => (
          <TableCell key={cell.id}>{cell.content}</TableCell>
        ))}
    </TableRow>
  );
};
